import type { AxiosResponse } from 'axios'
import axios from 'axios'

import type { PaginatedResponse } from '@/api/schemas/common/pagination'
import type {
    CooperantStockMaterial,
    CooperantStockMaterialType,
    CooperantStockMaterialTypeDictItem
} from '@/api/schemas/cooperantStocks/cooperantStocks'
import { API_ENDPOINTS } from '@/constants/apiEndpoints'

export interface CooperationJobStatus {
    id: number
    name: string
}

export interface CooperationJobListItem {
    id: number
    number: string
    orderNumbers: string[] | null
    shippingDate: string | null
    status: CooperationJobStatus | null
    hasUnreadComment: boolean
    unreadComment: string | null
}

export interface CooperationJobMaterial {
    id: number
    thickness: number | string | null
    weight: number | string | null
    name: string
}

export interface CooperationJobCommentAuthor {
    id: number
    firstName: string
    lastName: string
    email: string
    username: string
}

export interface CooperationJobComment {
    id: number
    cooperationJobId: number
    comment: string
    createdBy: CooperationJobCommentAuthor
    createdAt: string
    isActive: boolean
}

export interface CooperationJobMaterialConsumption {
    id: number
    material: CooperantStockMaterial
    materialType: CooperantStockMaterialType
    quantity: number
}

export interface CooperationJobMaterialConsumptionPayload {
    materialId: number
    quantity: number
}

export interface CooperationJob {
    id: number
    number: string
    orderNumbers: string[] | null
    shippingDate: string | null
    status: CooperationJobStatus
    paperConsumed: string | null
    materialConsumptions: CooperationJobMaterialConsumption[]
    printMethod: string | null
    material: CooperationJobMaterial | null
    formatA: number | null
    formatB: number | null
    netAmount: number | null
    wastePerSetup: number | null
    wastePerRun: number | null
    colorsSideA: string | null
    colorsSideB: string | null
    rasterType: string | null
    weight: number | null
    sheetCountStep: number | null
    canAddComment: boolean
    canReportMaterialConsumptions: boolean
    comments: CooperationJobComment[]
}

export type CooperationJobResponse = Omit<CooperationJob, 'canAddComment' | 'comments'> &
    Partial<Pick<CooperationJob, 'canAddComment' | 'comments'>>

export interface UpdateCooperationJobPayload {
    statusId?: number
    paperConsumed?: string | null
    // full state of the section: materials missing from the list are removed by the backend
    materialConsumptions?: CooperationJobMaterialConsumptionPayload[]
}

export interface AddCooperationJobCommentPayload {
    cooperationJobId: number
    comment: string
}

export type CooperationJobsListResponse = PaginatedResponse<CooperationJobListItem>
export type CooperationJobStatusesResponse = PaginatedResponse<CooperationJobStatus>
export type CooperationJobConsumptionMaterialTypesResponse = PaginatedResponse<CooperantStockMaterialTypeDictItem>

class CooperationJobsQueries {
    fetchCooperationJobs(queryString = ''): Promise<AxiosResponse<CooperationJobsListResponse>> {
        const url = queryString ? `${API_ENDPOINTS.COOPERATION_JOBS.LIST}?${queryString}` : API_ENDPOINTS.COOPERATION_JOBS.LIST

        return axios.get<CooperationJobsListResponse>(url)
    }

    fetchCooperationJobStatuses(): Promise<AxiosResponse<CooperationJobStatusesResponse>> {
        return axios.get<CooperationJobStatusesResponse>(API_ENDPOINTS.COOPERATION_JOBS.STATUSES)
    }

    fetchConsumptionMaterialTypes(): Promise<AxiosResponse<CooperationJobConsumptionMaterialTypesResponse>> {
        return axios.get<CooperationJobConsumptionMaterialTypesResponse>(
            API_ENDPOINTS.COOPERATION_JOBS.CONSUMPTION_MATERIAL_TYPES
        )
    }

    generateCooperationJobsReport(queryString = ''): Promise<AxiosResponse<unknown>> {
        const filtersQuery = queryString ? `&${queryString}` : ''

        return axios.get<unknown>(`${API_ENDPOINTS.COOPERATION_JOBS.LIST}?report=true${filtersQuery}`)
    }

    fetchCooperationJob(cooperationJobId: number | string): Promise<AxiosResponse<CooperationJobResponse>> {
        return axios.get<CooperationJobResponse>(API_ENDPOINTS.COOPERATION_JOBS.DETAILS(cooperationJobId))
    }

    updateCooperationJob(
        cooperationJobId: number | string,
        payload: UpdateCooperationJobPayload
    ): Promise<AxiosResponse<CooperationJobResponse>> {
        return axios.put<CooperationJobResponse>(API_ENDPOINTS.COOPERATION_JOBS.DETAILS(cooperationJobId), payload)
    }

    addCooperationJobComment(payload: AddCooperationJobCommentPayload): Promise<AxiosResponse<CooperationJobComment>> {
        return axios.post<CooperationJobComment>(API_ENDPOINTS.COOPERATION_JOBS.COMMENTS, payload)
    }

    deleteCooperationJob(cooperationJobId: number): Promise<AxiosResponse<void>> {
        return axios.delete<void>(API_ENDPOINTS.COOPERATION_JOBS.DETAILS(cooperationJobId))
    }

    printCooperationJobCards(cooperationJobId: number | string): Promise<AxiosResponse<ArrayBuffer>> {
        return axios.post<ArrayBuffer>(
            API_ENDPOINTS.COOPERATION_JOBS.ORDER_CARDS_PRINT,
            { cooperationJobId },
            { responseType: 'arraybuffer' }
        )
    }
}

export default new CooperationJobsQueries()
